import { createClient } from '@/lib/supabase/client';

export interface Apartment {
  id: string;
  name: string;
  description: string;
  location: string;
  price_per_night: number;
  capacity: number;
  bedrooms: number;
  bathrooms: number;
  beds: number;
  apartment_type: string;
  amenities: string[];
  is_available: boolean;
  is_featured: boolean;
  tag: string | null;
  cover_image_url: string | null;
  created_at: string;
  updated_at: string;
  apartment_images?: ApartmentImage[];
}

export interface ApartmentImage {
  id: string;
  apartment_id: string;
  storage_path: string;
  url: string;
  alt_text: string;
  is_primary: boolean;
  display_order: number;
  created_at: string;
}

export interface Booking {
  id: string;
  booking_reference: string;
  apartment_id: string;
  guest_name: string;
  guest_email: string;
  guest_phone: string;
  guest_message: string;
  num_guests: number;
  check_in_date: string;
  check_out_date: string;
  num_nights: number;
  price_per_night: number;
  total_price: number;
  booking_status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  payment_status: 'unpaid' | 'pending' | 'paid' | 'refunded';
  user_id: string | null;
  admin_notes: string;
  created_at: string;
  updated_at: string;
  apartments?: Apartment;
}

export interface CreateBookingData {
  apartment_id: string;
  guest_name: string;
  guest_email: string;
  guest_phone: string;
  guest_message?: string;
  num_guests: number;
  check_in_date: string;
  check_out_date: string;
  price_per_night: number;
  total_price: number;
}

// ─── Apartments ───────────────────────────────────────────────

export async function getApartments(featuredOnly = false): Promise<Apartment[]> {
  const supabase = createClient();
  let query = supabase
    .from('apartments')
    .select('*, apartment_images(*)')
    .order('is_featured', { ascending: false })
    .order('created_at', { ascending: false });

  // Only filter by is_featured when explicitly requested AND there are featured apartments
  // This prevents the guest homepage from showing nothing when no apartments are featured
  if (featuredOnly) {
    // First check if any featured apartments exist
    const { data: featuredCheck } = await supabase
      .from('apartments')
      .select('id')
      .eq('is_featured', true)
      .limit(1);
    
    if (featuredCheck && featuredCheck.length > 0) {
      query = query.eq('is_featured', true);
    }
    // If no featured apartments, fall through and return all apartments
  }

  const { data, error } = await query;
  if (error) {
    console.error('[getApartments] Supabase error:', error.code, error.message, error.details, error.hint);
    throw new Error(`Failed to load apartments: ${error.message} (code: ${error.code})`);
  }
  return data || [];
}

export async function getApartmentById(id: string): Promise<Apartment | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('apartments')
    .select('*, apartment_images(*)')
    .eq('id', id)
    .single();

  if (error) return null;
  return data;
}

export async function checkAvailability(
  apartmentId: string,
  checkIn: string,
  checkOut: string,
  excludeBookingId?: string
): Promise<boolean> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('check_apartment_availability', {
    p_apartment_id: apartmentId,
    p_check_in: checkIn,
    p_check_out: checkOut,
    p_exclude_booking_id: excludeBookingId || null,
  });

  if (error) throw error;
  return data as boolean;
}

// ─── Bookings ─────────────────────────────────────────────────

export async function createBooking(data: CreateBookingData): Promise<Booking> {
  const supabase = createClient();
  const { data: booking, error } = await supabase
    .from('bookings')
    .insert({
      ...data,
      booking_reference: '',
    })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return booking;
}

export async function getBookingByReference(ref: string): Promise<Booking | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('bookings')
    .select('*, apartments(*)')
    .eq('booking_reference', ref)
    .single();

  if (error) return null;
  return data;
}

// ─── Admin: Apartments ────────────────────────────────────────

export async function createApartment(
  apt: Omit<Apartment, 'id' | 'created_at' | 'updated_at' | 'apartment_images'>
): Promise<Apartment> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('apartments')
    .insert(apt)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function updateApartment(
  id: string,
  updates: Partial<Omit<Apartment, 'id' | 'created_at' | 'apartment_images'>>
): Promise<Apartment> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('apartments')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function deleteApartment(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('apartments').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Admin: Images ────────────────────────────────────────────

export async function uploadApartmentImage(
  apartmentId: string,
  file: File,
  isPrimary: boolean,
  displayOrder: number
): Promise<ApartmentImage> {
  const supabase = createClient();
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const timestamp = Date.now();
  const path = `${apartmentId}/${timestamp}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('apartment-images')
    .upload(path, file, { upsert: false, contentType: file.type });

  if (uploadError) {
    console.error('[uploadApartmentImage] Storage upload error:', uploadError);
    throw new Error(`Image upload failed: ${uploadError.message}`);
  }

  const { data: urlData } = supabase.storage
    .from('apartment-images')
    .getPublicUrl(path);

  // If this is the first/primary image, unset any existing primary images first
  if (isPrimary) {
    await supabase
      .from('apartment_images')
      .update({ is_primary: false })
      .eq('apartment_id', apartmentId);
  }

  const { data, error } = await supabase
    .from('apartment_images')
    .insert({
      apartment_id: apartmentId,
      storage_path: path,
      url: urlData.publicUrl,
      alt_text: `${apartmentId} image`,
      is_primary: isPrimary,
      display_order: displayOrder,
    })
    .select()
    .single();

  if (error) {
    console.error('[uploadApartmentImage] DB insert error:', error);
    throw new Error(`Failed to save image record: ${error.message}`);
  }

  // If primary, update apartment cover_image_url
  if (isPrimary) {
    await supabase
      .from('apartments')
      .update({ cover_image_url: urlData.publicUrl })
      .eq('id', apartmentId);
  }

  return data;
}

export async function deleteApartmentImage(imageId: string, storagePath: string): Promise<void> {
  const supabase = createClient();
  await supabase.storage.from('apartment-images').remove([storagePath]);
  const { error } = await supabase.from('apartment_images').delete().eq('id', imageId);
  if (error) throw new Error(error.message);
}

export async function setPrimaryImage(imageId: string, apartmentId: string): Promise<void> {
  const supabase = createClient();

  // First unset all primary images for this apartment
  const { error: unsetError } = await supabase
    .from('apartment_images')
    .update({ is_primary: false })
    .eq('apartment_id', apartmentId);
  if (unsetError) throw new Error(unsetError.message);

  // Then set the selected image as primary
  const { error } = await supabase
    .from('apartment_images')
    .update({ is_primary: true })
    .eq('id', imageId);
  if (error) throw new Error(error.message);
}

// ─── Admin: Bookings ──────────────────────────────────────────

export async function getAdminBookings(filters?: {
  status?: string;
  search?: string;
}): Promise<Booking[]> {
  const supabase = createClient();
  let query = supabase
    .from('bookings')
    .select('*, apartments(name, location)')
    .order('created_at', { ascending: false });

  if (filters?.status && filters.status !== 'all') {
    query = query.eq('booking_status', filters.status);
  }
  if (filters?.search) {
    query = query.or(
      `guest_name.ilike.%${filters.search}%,guest_email.ilike.%${filters.search}%,booking_reference.ilike.%${filters.search}%`
    );
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function updateBookingStatus(
  id: string,
  bookingStatus: Booking['booking_status'],
  paymentStatus?: Booking['payment_status']
): Promise<void> {
  const supabase = createClient();
  const updates: Record<string, string> = { booking_status: bookingStatus };
  if (paymentStatus) updates.payment_status = paymentStatus;

  const { error } = await supabase.from('bookings').update(updates).eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Admin: Dashboard Stats ───────────────────────────────────

export interface DashboardStats {
  totalApartments: number;
  availableApartments: number;
  totalBookings: number;
  pendingBookings: number;
  confirmedBookings: number;
  cancelledBookings: number;
  completedBookings: number;
  totalRevenue: number;
  revenueThisMonth: number;
}

export async function getDashboardStats(): Promise<DashboardStats> {
  const supabase = createClient();

  const [aptResult, bookingsResult] = await Promise.all([
    supabase.from('apartments').select('id, is_available'),
    supabase.from('bookings').select('id, booking_status, total_price, created_at'),
  ]);

  const apartments = aptResult.data || [];
  const bookings = bookingsResult.data || [];

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

  const paidStatuses: Booking['booking_status'][] = ['confirmed', 'completed'];

  return {
    totalApartments: apartments.length,
    availableApartments: apartments.filter((a) => a.is_available).length,
    totalBookings: bookings.length,
    pendingBookings: bookings.filter((b) => b.booking_status === 'pending').length,
    confirmedBookings: bookings.filter((b) => b.booking_status === 'confirmed').length,
    cancelledBookings: bookings.filter((b) => b.booking_status === 'cancelled').length,
    completedBookings: bookings.filter((b) => b.booking_status === 'completed').length,
    totalRevenue: bookings
      .filter((b) => paidStatuses.includes(b.booking_status))
      .reduce((sum, b) => sum + Number(b.total_price), 0),
    revenueThisMonth: bookings
      .filter(
        (b) =>
          paidStatuses.includes(b.booking_status) &&
          b.created_at >= startOfMonth
      )
      .reduce((sum, b) => sum + Number(b.total_price), 0),
  };
}

// ─── Owner: Apartment Assignments ─────────────────────────────

export interface OwnerApartmentAssignment {
  id: string;
  owner_id: string;
  apartment_id: string;
  assigned_by: string | null;
  created_at: string;
  apartments?: Apartment;
  user_profiles?: {
    id: string;
    full_name: string;
    email: string;
    phone: string;
    role: string;
  };
}

export interface OwnerProfile {
  id: string;
  email: string;
  full_name: string;
  phone: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

/** Get all apartments assigned to the currently logged-in property owner */
export async function getOwnerAssignedApartments(): Promise<Apartment[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('owner_apartment_assignments')
    .select('apartment_id, apartments(*, apartment_images(*))')
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return (data || []).map((row: any) => row.apartments).filter(Boolean);
}

/** Get bookings for apartments assigned to the currently logged-in property owner */
export async function getOwnerBookings(): Promise<Booking[]> {
  const supabase = createClient();
  // First get assigned apartment IDs
  const { data: assignments, error: assignError } = await supabase
    .from('owner_apartment_assignments')
    .select('apartment_id');

  if (assignError) throw new Error(assignError.message);
  const apartmentIds = (assignments || []).map((a: any) => a.apartment_id);
  if (apartmentIds.length === 0) return [];

  const { data, error } = await supabase
    .from('bookings')
    .select('*, apartments(name, location)')
    .in('apartment_id', apartmentIds)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return data || [];
}

// ─── Admin: Owner Management ──────────────────────────────────

/** Get all property owners */
export async function getPropertyOwners(): Promise<OwnerProfile[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('role', 'property_owner')
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return data || [];
}

/** Get all assignments (admin view) */
export async function getAllAssignments(): Promise<OwnerApartmentAssignment[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('owner_apartment_assignments')
    .select('*, apartments(id, name, location), user_profiles!owner_id(id, full_name, email, phone, role)')
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return data || [];
}

/** Assign an apartment to a property owner */
export async function assignApartmentToOwner(
  ownerId: string,
  apartmentId: string,
  assignedBy: string
): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('owner_apartment_assignments')
    .upsert(
      { owner_id: ownerId, apartment_id: apartmentId, assigned_by: assignedBy },
      { onConflict: 'apartment_id' }
    );
  if (error) throw new Error(error.message);
}

/** Remove an apartment assignment */
export async function removeApartmentAssignment(assignmentId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('owner_apartment_assignments')
    .delete()
    .eq('id', assignmentId);
  if (error) throw new Error(error.message);
}

/** Create a property owner account (admin action — uses signUp via client) */
export async function createPropertyOwnerProfile(
  id: string,
  email: string,
  fullName: string,
  phone: string
): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('user_profiles')
    .upsert({
      id,
      email,
      full_name: fullName,
      phone,
      role: 'property_owner',
      is_active: true,
    });
  if (error) throw new Error(error.message);
}
